import { isRedirect, redirect } from '@tanstack/react-router';
import type { PlatformWhoami } from '@vagusx/clients';
import { getPlatformClient } from '@/lib/api-clients';
import { authClient } from '@/lib/auth-client';
import { CACHE_TIME_5_MINS, queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';

/**
 * Route-guard reads of "who is signed in", cached in the shared query client.
 *
 * Why cached: TanStack Router re-runs every `beforeLoad` in the matched chain
 * on EVERY navigation, including a search-param-only one — which is how every
 * detail sheet in the app opens and closes (`?id=…`). Uncached, the
 * `_authenticated` guard made up to three serial round trips (session →
 * whoami → passkeys, ~1–1.5 s in prod) before the router would commit, so each
 * sheet took that long to open and again to close.
 *
 * Staleness: every path that changes who is signed in already resets this
 * cache — sign-out and start-impersonation `clear()` it, sign-in and passkey
 * verification `invalidateQueries()` it, stop-impersonation is a full page
 * load. These reads use `fetchQuery`, which refetches an invalidated entry;
 * `ensureQueryData` would hand back the stale one, so don't swap it in. What
 * the window does cost: a session that expires or is revoked server-side keeps
 * passing the UI guard for up to five minutes. The API still rejects every
 * request in that window, and its 401 sends the browser to /login carrying
 * the page it was on (`loginRedirectUrl` in the API client).
 *
 * No retries: the app-wide default (`retry: 1`) would hold a navigation for a
 * second on every failed guard call, and each failure here already has an
 * answer (redirect, or fall through) that the uncached guard gave at once.
 */
const GUARD_QUERY = { staleTime: CACHE_TIME_5_MINS, retry: false } as const;

/**
 * How long a cached whoami may be trusted: the guard window, but never past
 * the moment an impersonation it vouches for expires — after that the next
 * navigation must ask again and bounce the admin to the platform home, as the
 * uncached guard did.
 */
export function whoamiStaleTime(data: PlatformWhoami | undefined, dataUpdatedAt: number): number {
  const expiresAt = data?.impersonationExpiresAt ? Date.parse(data.impersonationExpiresAt) : NaN;
  if (!data?.impersonating || Number.isNaN(expiresAt)) return CACHE_TIME_5_MINS;
  return Math.max(0, Math.min(CACHE_TIME_5_MINS, expiresAt - dataUpdatedAt));
}

/**
 * The resets above only reach the tab that signed in or out. Signing in as
 * someone else in ANOTHER tab would leave this tab's guard answering for the
 * previous user — skipping their passkey prompt — for the rest of the window.
 * Coming back to a tab marks the session and whoami stale, so the next
 * navigation asks again. `refetchType: 'none'`: nothing refetches until a
 * guard runs, so the banner's whoami observer isn't woken on every tab switch.
 */
export function revalidateGuardOnReturn(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.auth.all, refetchType: 'none' });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.platform.whoami(),
    refetchType: 'none',
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', (): void => {
    if (document.visibilityState === 'visible') revalidateGuardOnReturn();
  });
}

export type AuthSession = typeof authClient.$Infer.Session;

/** The current session, or null when signed out. A null is never cached. */
export async function fetchGuardSession(): Promise<AuthSession | null> {
  const session = await queryClient.fetchQuery({
    queryKey: queryKeys.auth.session(),
    queryFn: async (): Promise<AuthSession | null> => {
      const result = await authClient.getSession({ fetchOptions: { credentials: 'include' } });
      return result.data ?? null;
    },
    ...GUARD_QUERY,
  });
  // Keeping "signed out" would bounce the next sign-in back to /login on any
  // path that forgot to invalidate — a loop. Only a positive answer is kept.
  if (!session) queryClient.removeQueries({ queryKey: queryKeys.auth.session() });
  return session;
}

/**
 * The platform principal's whoami. Throws for org principals (403) — a failed
 * fetch is not cached, so that answer is asked again on the next navigation.
 * Shares its cache entry with `ImpersonationBanner` and `useOpsMode`.
 *
 * Deliberately no AbortSignal: once a queryFn reads the signal, query-core
 * cancels the in-flight fetch when the entry's last observer unmounts, and a
 * cancelled guard fetch would read as a 403 and misroute the user.
 */
export function fetchGuardWhoami(): Promise<PlatformWhoami> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.platform.whoami(),
    queryFn: (): Promise<PlatformWhoami> => getPlatformClient().whoami(),
    ...GUARD_QUERY,
    staleTime: (query): number => whoamiStaleTime(query.state.data, query.state.dataUpdatedAt),
  });
}

/**
 * Whether the user has registered any passkey. Throws on an API error rather
 * than answering "none": caching a failure as "no passkeys" would skip the
 * 2FA prompt for the whole stale window instead of for one navigation.
 * `ProfilePage` invalidates `passkeys.all` on add/delete, which covers this.
 */
export function fetchGuardHasPasskeys(userId: string): Promise<boolean> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.passkeys.exists(userId),
    queryFn: async (): Promise<boolean> => {
      const result = await authClient.passkey.listUserPasskeys();
      if (result.error) throw new Error(result.error.message ?? 'Failed to list passkeys');
      return (result.data ?? []).length > 0;
    },
    ...GUARD_QUERY,
  });
}

/** `beforeLoad` for the `_authenticated` layout route. */
export async function requireAuthenticatedUser(location: {
  pathname: string;
  href: string;
}): Promise<void> {
  const session = await fetchGuardSession();
  if (!session) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }

  const user = session.user as Record<string, unknown>;

  // Platform principals don't have an org; they should be at /platform/*.
  // A platform principal with an ACTIVE impersonation session is allowed to
  // access org routes (the whole point of impersonation) — we detect that
  // by asking the server `whoami`; if `impersonating=true`, let them
  // through. Otherwise bounce to the platform home.
  if (!user.organizationId) {
    try {
      const whoami = await fetchGuardWhoami();
      if (!whoami.impersonating) {
        throw redirect({ to: '/platform/organizations' });
      }
    } catch (err) {
      if (isRedirect(err)) throw err;
      // whoami returned 403 / 404 — this is an org user without an org,
      // meaning their signup is incomplete.
      throw redirect({ to: '/complete-profile' });
    }
  }

  // Skip 2FA check on the verification page itself
  if (location.pathname === '/verify-passkey') return;

  // Check if user needs passkey 2FA verification
  const isVerified = sessionStorage.getItem('twoFactorVerified') === 'true';
  if (!isVerified) {
    let hasPasskeys = false;
    try {
      hasPasskeys = await fetchGuardHasPasskeys(session.user.id);
    } catch {
      // API error — don't block access (unchanged from the uncached guard)
    }
    if (hasPasskeys) {
      throw redirect({ to: '/verify-passkey' });
    }
  }
}

/** `beforeLoad` for the `/platform` layout route. */
export async function requirePlatformPrincipal(location: { href: string }): Promise<void> {
  const session = await fetchGuardSession();
  if (!session) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }

  try {
    await fetchGuardWhoami();
  } catch {
    // 403 means org principal hit /platform — bounce to /.
    // Any other error we also bounce; platform principals are a small set
    // and any misconfiguration should surface as "you shouldn't be here".
    throw redirect({ to: '/' });
  }
}
